/**
 * In-memory fallback `ConversationStore` (Requirement 12.2).
 *
 * Used when IndexedDB is unavailable at boot — for example in private
 * browsing contexts, embedded WebViews, or browsers where the user has
 * disabled storage. The boot sequence checks `OpenStoreResult.inMemoryFallback`
 * and surfaces a sticky banner when this adapter is active so the
 * End_User knows messages will not survive closing the tab.
 *
 * Behaviour mirrors `./dexie.ts` exactly: the same `wipeAll` reset
 * (Property 10), the same import atomicity (Property 22), and the
 * same singleton-key semantics for profile / preferences /
 * companionName / apiKey. All methods are async by signature and
 * resolve synchronously via `Promise.resolve(...)` so the call sites
 * read identically regardless of which adapter is active.
 *
 * Backed by plain `Map` instances and an array; no I/O, no globals
 * beyond `crypto.randomUUID` and `Date.now`, both of which can be
 * injected for deterministic property tests (see `createInMemoryStoreWithDeps`).
 */

import type { RouterLogEntry } from '../../domain/modelRouter/types';
import {
  defaultPreferences,
  seedDefaultProfile,
} from '../../domain/types';
import type {
  ConversationThread,
  ConversationThreadSummary,
  ExportBundle,
  ImportResult,
  Message,
  Preferences,
  ProfileContext,
} from '../../domain/types';

import type { ConversationStore, QueuedMessageRow } from './types';
import { validateExportBundle } from './validator';

const META_KEYS = {
  profile: 'profile',
  preferences: 'preferences',
  companionName: 'companionName',
  apiKey: 'apiKey',
} as const;

/** Maximum number of diagnostics entries retained in the ring buffer. */
const DIAGNOSTICS_CAP = 200;

/** Internal injection seam for the timestamp + uuid providers. */
export interface InMemoryStoreDeps {
  nowMs: () => number;
  uuid: () => string;
}

const defaultDeps: InMemoryStoreDeps = {
  nowMs: () => Date.now(),
  uuid: () => crypto.randomUUID(),
};

/**
 * Deep-clone a plain JSON-shaped value so reads return immutable
 * snapshots independent of internal storage. Avoids `structuredClone`
 * to keep the bundle small and to preserve TypeScript types through
 * the JSON round-trip.
 */
function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function projectSummary(thread: ConversationThread): ConversationThreadSummary {
  return {
    id: thread.id,
    title: thread.title,
    updatedAt: thread.updatedAt,
    messageCount: thread.messages.length,
  };
}

/**
 * Internal factory accepting injected dependencies. Exposed for tests
 * that need a deterministic clock + uuid (Property 7).
 */
export function createInMemoryStoreWithDeps(
  deps: InMemoryStoreDeps,
): ConversationStore {
  const meta = new Map<string, unknown>();
  const threads = new Map<string, ConversationThread>();
  const queue = new Map<string, QueuedMessageRow>();
  const diagnostics: RouterLogEntry[] = [];

  const getMeta = <T>(key: string): T | null => {
    if (!meta.has(key)) return null;
    const v = meta.get(key);
    if (v === null || v === undefined) return null;
    return cloneJson(v) as T;
  };

  const setMeta = (key: string, value: unknown): void => {
    if (value === null) {
      meta.set(key, null);
    } else {
      meta.set(key, cloneJson(value));
    }
  };

  const requireThread = (id: string): ConversationThread => {
    const t = threads.get(id);
    if (!t) {
      throw new Error(`Thread ${id} not found`);
    }
    return t;
  };

  return {
    // ---------- Threads ----------

    listThreads(): Promise<ConversationThreadSummary[]> {
      const summaries = Array.from(threads.values())
        .slice()
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map(projectSummary);
      return Promise.resolve(summaries);
    },

    getThread(id: string): Promise<ConversationThread | null> {
      const t = threads.get(id);
      return Promise.resolve(t ? cloneJson(t) : null);
    },

    createThread(title: string): Promise<ConversationThread> {
      const now = deps.nowMs();
      const thread: ConversationThread = {
        id: deps.uuid(),
        title,
        createdAt: now,
        updatedAt: now,
        messages: [],
        rollingSummary: null,
        summarizedThroughMessageId: null,
      };
      threads.set(thread.id, cloneJson(thread));
      return Promise.resolve(cloneJson(thread));
    },

    renameThread(id: string, title: string): Promise<void> {
      const t = requireThread(id);
      t.title = title;
      t.updatedAt = deps.nowMs();
      return Promise.resolve();
    },

    deleteThread(id: string): Promise<void> {
      threads.delete(id);
      return Promise.resolve();
    },

    clearThread(id: string): Promise<void> {
      const t = requireThread(id);
      t.messages = [];
      t.rollingSummary = null;
      t.summarizedThroughMessageId = null;
      t.updatedAt = deps.nowMs();
      return Promise.resolve();
    },

    appendMessage(threadId: string, message: Message): Promise<void> {
      const t = requireThread(threadId);
      t.messages.push(cloneJson(message));
      t.updatedAt = deps.nowMs();
      return Promise.resolve();
    },

    updateMessage(
      threadId: string,
      messageId: string,
      patch: Partial<Message>,
    ): Promise<void> {
      const t = requireThread(threadId);
      const idx = t.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) {
        return Promise.reject(
          new Error(`Message ${messageId} not in thread ${threadId}`),
        );
      }
      const existing = t.messages[idx];
      if (existing === undefined) {
        return Promise.reject(
          new Error(`Message ${messageId} not in thread ${threadId}`),
        );
      }
      // id and threadId are immutable; clone the patch so callers can
      // safely retain references to the original object.
      const merged: Message = {
        ...existing,
        ...cloneJson(patch),
        id: existing.id,
        threadId: existing.threadId,
      };
      t.messages[idx] = merged;
      t.updatedAt = deps.nowMs();
      return Promise.resolve();
    },

    setRollingSummary(
      threadId: string,
      summary: string,
      summarizedThroughMessageId: string,
    ): Promise<void> {
      const t = requireThread(threadId);
      t.rollingSummary = summary;
      t.summarizedThroughMessageId = summarizedThroughMessageId;
      // Mark every message up to and including the cut-off as summarized.
      // Messages are stored in chronological order, so we walk forward
      // until we find the cut-off id.
      let crossed = false;
      for (const m of t.messages) {
        if (!crossed) {
          m.summarized = true;
          if (m.id === summarizedThroughMessageId) {
            crossed = true;
          }
        }
      }
      t.updatedAt = deps.nowMs();
      return Promise.resolve();
    },

    // ---------- Profile / preferences / companion name ----------

    getProfile(): Promise<ProfileContext | null> {
      return Promise.resolve(getMeta<ProfileContext>(META_KEYS.profile));
    },

    setProfile(p: ProfileContext): Promise<void> {
      setMeta(META_KEYS.profile, p);
      return Promise.resolve();
    },

    getPreferences(): Promise<Preferences | null> {
      return Promise.resolve(getMeta<Preferences>(META_KEYS.preferences));
    },

    setPreferences(p: Preferences): Promise<void> {
      setMeta(META_KEYS.preferences, p);
      return Promise.resolve();
    },

    getCompanionName(): Promise<string | null> {
      const v = meta.get(META_KEYS.companionName);
      if (v === undefined) return Promise.resolve(null);
      return Promise.resolve(v as string | null);
    },

    setCompanionName(name: string | null): Promise<void> {
      meta.set(META_KEYS.companionName, name);
      return Promise.resolve();
    },

    // ---------- API key ----------

    getApiKey(): Promise<string | null> {
      const v = meta.get(META_KEYS.apiKey);
      if (v === undefined) return Promise.resolve(null);
      return Promise.resolve(v as string | null);
    },

    setApiKey(key: string | null): Promise<void> {
      meta.set(META_KEYS.apiKey, key);
      return Promise.resolve();
    },

    // ---------- Queue ----------

    enqueueMessage(row: QueuedMessageRow): Promise<void> {
      queue.set(row.id, cloneJson(row));
      return Promise.resolve();
    },

    listQueuedMessages(): Promise<QueuedMessageRow[]> {
      const rows = Array.from(queue.values())
        .slice()
        .sort((a, b) => a.enqueuedAt - b.enqueuedAt)
        .map((r) => cloneJson(r));
      return Promise.resolve(rows);
    },

    removeQueuedMessage(id: string): Promise<void> {
      queue.delete(id);
      return Promise.resolve();
    },

    updateQueuedMessage(
      id: string,
      patch: Partial<QueuedMessageRow>,
    ): Promise<void> {
      const existing = queue.get(id);
      if (!existing) {
        return Promise.reject(new Error(`Queued message ${id} not found`));
      }
      // id is immutable.
      const merged: QueuedMessageRow = {
        ...existing,
        ...cloneJson(patch),
        id: existing.id,
      };
      queue.set(id, merged);
      return Promise.resolve();
    },

    // ---------- Diagnostics ----------

    appendLogEntry(entry: RouterLogEntry): Promise<void> {
      diagnostics.push(cloneJson(entry));
      while (diagnostics.length > DIAGNOSTICS_CAP) {
        diagnostics.shift();
      }
      return Promise.resolve();
    },

    listLogEntries(limit: number): Promise<RouterLogEntry[]> {
      const safeLimit = Math.max(0, Math.floor(limit));
      const sortedDesc = diagnostics
        .slice()
        .sort((a, b) => b.seq - a.seq)
        .slice(0, safeLimit);
      // Caller wants chronological order, so reverse the desc slice.
      return Promise.resolve(sortedDesc.reverse().map((e) => cloneJson(e)));
    },

    // ---------- Bulk ----------

    async exportAll(): Promise<ExportBundle> {
      const profile =
        getMeta<ProfileContext>(META_KEYS.profile) ?? seedDefaultProfile();
      const preferences =
        getMeta<Preferences>(META_KEYS.preferences) ?? defaultPreferences();
      const companionNameRaw = meta.get(META_KEYS.companionName);
      const companionName =
        companionNameRaw === undefined
          ? null
          : (companionNameRaw as string | null);
      const allThreads = Array.from(threads.values())
        .slice()
        .sort((a, b) => a.createdAt - b.createdAt)
        .map((t) => cloneJson(t));
      return {
        schemaVersion: 1,
        exportedAt: deps.nowMs(),
        profile,
        preferences,
        companionName,
        threads: allThreads,
      };
    },

    async importAll(bundle: unknown): Promise<ImportResult> {
      const result = validateExportBundle(bundle);
      if (!result.ok) {
        // No mutation happened — return validation error verbatim
        // (Property 22).
        return { ok: false, errorPath: result.errorPath, reason: result.reason };
      }
      const valid = result.bundle;
      // Replace meta singletons + threads atomically. In-memory
      // operations are synchronous so atomicity is trivial — we still
      // mirror the Dexie semantics by rebuilding all maps in one pass.
      meta.set(META_KEYS.profile, cloneJson(valid.profile));
      meta.set(META_KEYS.preferences, cloneJson(valid.preferences));
      meta.set(META_KEYS.companionName, valid.companionName);
      threads.clear();
      for (const t of valid.threads) {
        threads.set(t.id, cloneJson(t));
      }
      return { ok: true, importedThreadCount: valid.threads.length };
    },

    async wipeAll(): Promise<void> {
      meta.clear();
      threads.clear();
      queue.clear();
      diagnostics.length = 0;
      // Re-seed defaults so the next boot finds a valid profile +
      // preferences and the Naming_Flow re-arms because companionName
      // is null (Property 10).
      meta.set(META_KEYS.profile, seedDefaultProfile());
      meta.set(META_KEYS.preferences, defaultPreferences());
      meta.set(META_KEYS.companionName, null);
    },
  };
}

/**
 * Public factory — uses real `Date.now()` and `crypto.randomUUID()`.
 *
 * Tests that need determinism call `createInMemoryStoreWithDeps`
 * directly with a fake clock and a counter-based uuid.
 */
export function createInMemoryStore(): ConversationStore {
  return createInMemoryStoreWithDeps(defaultDeps);
}
